import styled, { keyframes, css } from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";

// 개별 토스트 애니메이션 (position: relative이므로 translateX(-50%) 불필요)
const appearTop = keyframes`
  0% { transform: translateY(-20px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

const disappearTop = keyframes`
  0% { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(-20px); opacity: 0; }
`;

const appearBottom = keyframes`
  0% { transform: translateY(20px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

const disappearBottom = keyframes`
  0% { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(20px); opacity: 0; }
`;

const appearRight = keyframes`
  0% { transform: translateX(30px); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
`;

const disappearRight = keyframes`
  0% { transform: translateX(0); opacity: 1; }
  100% { transform: translateX(30px); opacity: 0; }
`;

export type ToastPosition = "top" | "bottom" | "top-right" | "bottom-right";

// 그룹 컨테이너: position별로 토스트를 묶는 wrapper
interface ToastGroupContainerProps {
  position: ToastPosition;
}

const getGroupPositionStyles = (position: ToastPosition) => {
  switch (position) {
    case "top":
      return css`
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        align-items: center;
      `;
    case "bottom":
      return css`
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        align-items: center;
        flex-direction: column-reverse;
      `;
    case "top-right":
      return css`
        top: 20px;
        right: 20px;
        align-items: flex-end;
      `;
    case "bottom-right":
      return css`
        bottom: 20px;
        right: 20px;
        align-items: flex-end;
        flex-direction: column-reverse;
      `;
  }
};

export const ToastGroupContainer = styled.div<ToastGroupContainerProps>`
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 10000;
  pointer-events: none;

  ${(props) => getGroupPositionStyles(props.position)}
`;

// 위치에 따른 애니메이션 결정
const getAnimation = (position: ToastPosition, isExiting: boolean) => {
  if (position === "top-right" || position === "bottom-right") {
    return css`
      animation: ${isExiting ? disappearRight : appearRight} 0.5s ease-in-out forwards;
    `;
  }
  if (position === "bottom") {
    return css`
      animation: ${isExiting ? disappearBottom : appearBottom} 0.5s ease-in-out forwards;
    `;
  }
  return css`
    animation: ${isExiting ? disappearTop : appearTop} 0.5s ease-in-out forwards;
  `;
};

interface ToastContainerProps {
  position: ToastPosition;
  isExiting: boolean;
  hasClickHandler?: boolean;
}

export const ToastContainer = styled.div<ToastContainerProps>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  padding-right: 40px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  min-width: 320px;
  max-width: 80vw;
  text-align: center;
  pointer-events: auto;
  cursor: ${(props) => (props.hasClickHandler ? "pointer" : "default")};

  ${(props) => getAnimation(props.position, props.isExiting)}

  &:hover {
    ${(props) => props.hasClickHandler && `box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);`}
  }
`;

export const ToastContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  line-height: 1.5;
`;

export const CloseButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  transition: all 0.2s;
  padding: 0;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[100]};
    color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  }

  &:active {
    background: ${COLOR_SYSTEM.get("Gray")?.[200]};
  }
`;
